import * as React from "react";
import { useLoaderData, useNavigate} from 'react-router-dom';
import Container from "@mui/material/Container";
import Grid from "@mui/material/Unstable_Grid2";
import AddIcon from "@mui/icons-material/Add";
import Header from "../components/Header";
import Subheader from "../components/Subheader";
import { listAnimals } from "../models/animals";
import { listAssessments } from "../models/assessments";
import { listChanges } from "../models/changes";
import AssessmentCard from "../components/cards/AssessmentCard";
import Filter from "../components/Filter";
import EwpFab from "../components/EwpFab";
import EwpButton from "../components/EwpButton";

import { useSectionB } from '../context/SectionBProvider';
import { name as speciesName } from '../utils/species';
import {useMessage} from "../context/MessageProvider";

export async function loader() {
  const animals = await listAnimals();
  const assessments = await listAssessments();
  const changes = await listChanges();
  return { animals, assessments, changes };
}

export default function AssessmentsListPage({ title = "Assessments", ...props }) {

  const { animals, assessments, changes} = useLoaderData();
  const navigate = useNavigate();
  const {setSuccess} = useMessage();

  const {inProgressObservations, current, clearObservations, startClipboardMode} = useSectionB();

  const animalById = React.useMemo(() => {
    return (animals || []).reduce((acc, cur) => {
      acc[cur.id] = cur;
      return acc;
    }, {});
  }, [animals]);

  const assessmentsWithAnimal = React.useMemo(() => {
    return (assessments || []).map((assessment) => {
      return {
        ...assessment,
        animal: animalById[assessment.animal_id] || null,
      };
    });
  }, [assessments, animalById]);

  const inProgressAssessments = React.useMemo(() => {
    return assessmentsWithAnimal.filter((cur) => !cur.submitted_on);
  }, [assessmentsWithAnimal]);

  const completedAssessments = React.useMemo(() => {
    return assessmentsWithAnimal.filter((cur) => !!cur.submitted_on);
  }, [assessmentsWithAnimal]);

  const [filteredInProgressAssessments, setFilteredInProgressAssessments] = React.useState(inProgressAssessments);
  const [filteredCompletedAssessments, setFilteredCompletedAssessments] = React.useState(completedAssessments);

  const speciesOptions = React.useMemo(() => {
    const values = Array.from(
      new Set((animals || []).map((cur) => String(cur.species || '').trim()).filter(Boolean))
    );
    values.sort((a, b) => a.localeCompare(b));
    return values.map((value) => ({ value, label: speciesName(value) }));
  }, [animals]);

  const locationOptions = React.useMemo(() => {
    const values = Array.from(
      new Set((animals || []).map((cur) => String(cur.location || '').trim()).filter(Boolean))
    );
    values.sort((a, b) => a.localeCompare(b));
    return values.map((value) => ({ value, label: value }));
  }, [animals]);

  const filterOptions = React.useMemo(() => ([
    {
      key: 'animal_name',
      label: 'Animal Name',
      type: 'text',
      getValue: (_value, record) => record?.animal?.name || ''
    },
    {
      key: 'species',
      label: 'Species',
      type: 'single-select',
      list: speciesOptions,
      getValue: (_value, record) => record?.animal?.species || ''
    },
    {
      key: 'location',
      label: 'Location',
      type: 'single-select',
      list: locationOptions,
      getValue: (_value, record) => record?.animal?.location || ''
    },
    {
      key: 'started_on_after',
      label: 'Started On (On or After)',
      type: 'date-after',
      recordkey: 'started_on'
    },
    {
      key: 'started_on_before',
      label: 'Started On (On or Before)',
      type: 'date-before',
      recordkey: 'started_on'
    },
    {
      key: 'submitted_on_after',
      label: 'Submitted On (On or After)',
      type: 'date-after',
      recordkey: 'submitted_on'
    },
    {
      key: 'submitted_on_before',
      label: 'Submitted On (On or Before)',
      type: 'date-before',
      recordkey: 'submitted_on'
    },
  ]), [speciesOptions, locationOptions]);

  const sortOptions = React.useMemo(() => ([
    {
      key: 'animal_name',
      label: 'Animal Name',
      default: 'asc',
      getValue: (_value, record) => record?.animal?.name || ''
    },
    {
      key: 'species',
      label: 'Species',
      getValue: (_value, record) => record?.animal?.species || ''
    },
    {
      key: 'started_on',
      label: 'Started On',
      getValue: (value) => value
    },
    {
      key: 'submitted_on',
      label: 'Submitted On',
      getValue: (value) => value
    },
  ]), []);

  /*
  const inProgressAssessments = assessments.filter((cur)=>{
    return cur.started_on && !cur.submitted_on;
  });

  const completeAssessments = assessments.filter((cur)=>{
    return cur.started_on && cur.submitted_on;
  });
  */

  const handleStartObservations = () => {
    startClipboardMode();
    navigate("/assessments/" + current.assessmentId + "/sectionb/" + current.id);
  }

  const handleClearObservations = () => {
    clearObservations();
    setSuccess("Section B observation clipboard cleared");
  }

  const navigateToAssessment = (id) => {
    console.log("assessment id: ", id);
    navigate("/assessments/" + id);
  }

  return (
    <Container component="main" maxWidth="md">
    <Header label={title} />

    <Subheader label="New Assessment" />
    <Grid container spacing={2}>
        <Grid xs={12} sm={4}>
          <EwpButton onClick={() => navigate("/assessments/new")}>Start New Assessment</EwpButton>
        </Grid>
    </Grid>


    {inProgressObservations.length > 0 && <>
      <Subheader label="Section B Observations Clipboard" />
      <Grid container spacing={2}>
        <Grid xs={12}>
          {inProgressObservations.map((cur)=>{
            return cur.title
          }).join(", ")}

        </Grid>
        <Grid xs={12} sm={4}>
          <EwpButton onClick={handleStartObservations}>Start Observations</EwpButton>
        </Grid>
        <Grid xs={12} sm={4}>
          <EwpButton onClick={handleClearObservations}>Clear Clipboard</EwpButton>
        </Grid>
      </Grid>
    </>}

    <Subheader label="Current Assessments" />

    <h3>In Progress</h3>
    <Filter
      records={inProgressAssessments}
      setFilteredRecords={setFilteredInProgressAssessments}
      filterOptions={filterOptions}
      sortOptions={sortOptions}
      badgeColor="success"
    />
    <Grid container spacing={2} sx={{mt: 1}}>
      {filteredInProgressAssessments.map((assessmentRecord) => {
        return (
          <Grid key={assessmentRecord.id} xs={12} sm={6} md={4}>
            <AssessmentCard
              animal={assessmentRecord.animal}
              assessment={assessmentRecord}
              changes={changes}
              showReportVisibility
              onActionAreaClick={() => {
                navigateToAssessment(assessmentRecord.id);
              }}
            />
          </Grid>
        );
      })}
    </Grid>

    <h3>Completed</h3>
    <Filter
      records={completedAssessments}
      setFilteredRecords={setFilteredCompletedAssessments}
      filterOptions={filterOptions}
      sortOptions={sortOptions}
      badgeColor="success"
    />
    <Grid container spacing={2} sx={{mt: 1}}>
      {filteredCompletedAssessments.map((assessmentRecord) => {
        return (
          <Grid key={assessmentRecord.id} xs={12} sm={6} md={4}>
            <AssessmentCard
              animal={assessmentRecord.animal}
              assessment={assessmentRecord}
              changes={changes}
              showReportVisibility
              onActionAreaClick={() => {
                navigateToAssessment(assessmentRecord.id);
              }}
            />
          </Grid>
        );
      })}
    </Grid>
    <EwpFab onClick={() => navigate("/assessments/new")}>
      <AddIcon />
    </EwpFab>
  </Container>
  );
}
